import type { MakeT, MakeLogoT } from "@/app/types/makes";

type MakeWithLogoT = MakeT & { make_logo: MakeLogoT | null };

/**
 * Check if a make name contains "service" (case-insensitive)
 *
 * @param makeName - The name of the make to check
 * @returns True if the make name contains "service", false otherwise
 */
function containsService(makeName: string | null): boolean {
  if (!makeName) return false;
  return makeName.toLowerCase().includes("service");
}

/**
 * Filter out makes that contain "service" in their name
 *
 * @param makes - Array of makes to filter
 * @returns Filtered array without makes containing "service" in the name
 *
 * @example
 * ```tsx
 * const filteredMakes = filterServiceMakes(makes);
 * ```
 */
export function filterServiceMakes<T extends MakeWithLogoT>(
  makes: T[] | null | undefined
): T[] {
  if (!makes) return [];
  return makes.filter((make) => !containsService(make.name));
}

/**
 * Filter out make names that contain "service" in their name
 *
 * @param makeNames - Array of make names to filter
 * @returns Filtered array without make names containing "service"
 *
 * @example
 * ```tsx
 * const filteredMakeNames = filterServiceMakeNames(makeNames);
 * ```
 */
export function filterServiceMakeNames(
  makeNames: (string | null)[] | null | undefined
): string[] {
  if (!makeNames) return [];
  return makeNames
    .filter((name): name is string => Boolean(name))
    .filter((name) => !containsService(name));
}
